import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminApi } from "@/lib/adminApi";

const fmt = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export async function openQuotationPdf(id) {
  const win = window.open("", "_blank");
  try {
    const { data } = await adminApi.get(`/admin/quotations/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    if (win) win.location.href = url; else window.location.href = url;
  } catch {
    if (win) win.close();
    toast.error("Could not open the PDF");
  }
}

function LineRow({ line, onChange, onRemove, i }) {
  return (
    <div className="grid grid-cols-[1fr_80px_110px_100px_32px] gap-2 items-center" data-testid={`quotation-line-${i}`}>
      <Input value={line.description} onChange={(e) => onChange({ ...line, description: e.target.value })} data-testid={`quotation-desc-${i}`} />
      <Input type="number" min="1" value={line.quantity} onChange={(e) => onChange({ ...line, quantity: Math.max(1, Number(e.target.value) || 1) })} data-testid={`quotation-qty-${i}`} />
      <Input type="number" min="0" step="0.01" value={line.unit_price} onChange={(e) => onChange({ ...line, unit_price: e.target.value })} data-testid={`quotation-price-${i}`} />
      <div className="text-right text-sm font-medium">{fmt(line.quantity * Number(line.unit_price || 0))}</div>
      <button type="button" onClick={onRemove} aria-label="Remove line" className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export function QuotationDialog({ quote, onClose, onCreated }) {
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!quote) return;
    setNotes("");
    adminApi.get(`/admin/quotes/${quote.id}/draft`).then(({ data }) => setLines(data.lines)).catch(() => toast.error("Could not load draft"));
  }, [quote]);

  const subtotal = lines.reduce((s, l) => s + l.quantity * Number(l.unit_price || 0), 0);
  const vat = subtotal * 0.18;

  const create = async () => {
    if (!lines.length || lines.some((l) => !l.description.trim())) return toast.error("Each line needs a description");
    setSaving(true);
    try {
      const { data } = await adminApi.post(`/admin/quotes/${quote.id}/quotation`, { lines: lines.map((l) => ({ ...l, unit_price: Number(l.unit_price || 0) })), notes });
      toast.success(`Quotation ${data.number} created`);
      onCreated(data);
    } catch {
      toast.error("Could not create quotation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quotation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-brand">Create quotation · {quote?.ref}</DialogTitle>
          <DialogDescription>Prices are pre-filled from the shop (with bulk discounts). Edit anything, then create the PDF. All amounts in USD; 18% VAT is added.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[1fr_80px_110px_100px_32px] gap-2 text-xs font-semibold text-muted-foreground">
          <span>Description</span><span>Qty</span><span>Unit (USD)</span><span className="text-right">Amount</span><span />
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <LineRow key={i} i={i} line={l} onChange={(nl) => setLines(lines.map((x, j) => (j === i ? nl : x)))} onRemove={() => setLines(lines.filter((_, j) => j !== i))} />
          ))}
        </div>
        <Button variant="outline" size="sm" className="w-fit" onClick={() => setLines([...lines, { description: "", quantity: 1, unit_price: 0 }])} data-testid="quotation-add-line"><Plus className="h-4 w-4 mr-1" />Add line</Button>
        <div className="ml-auto w-64 text-sm space-y-1" data-testid="quotation-totals">
          <div className="flex justify-between"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
          <div className="flex justify-between"><span>VAT (18%)</span><span>{fmt(vat)}</span></div>
          <div className="flex justify-between font-bold text-brand border-t pt-1"><span>Total (USD)</span><span data-testid="quotation-total">{fmt(subtotal + vat)}</span></div>
        </div>
        <Textarea placeholder="Notes for the customer (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quotation-notes" />
        <Button onClick={create} disabled={saving} className="bg-brand hover:bg-brand-700 text-white" data-testid="quotation-create">{saving ? "Creating…" : "Create PDF quotation"}</Button>
      </DialogContent>
    </Dialog>
  );
}
